import { AppEnum, DateFormatHelper, formatName, SoNumber, NeedEnums, OptionHelper, RecordMap } from "@/Shared";

import { CityModel, SectorModel, ContactModel } from '@/Domains';
import { NeedModel } from '../NeedModel';
import { NeedFormValues } from "../NeedFormValidation";


/**
 * =====================================================
 * Params for NeedFormViewModel
 * =====================================================
 *
 * Encapsulates all data required to build the form view model:
 * - enums used in dropdowns
 * - related domain lists (contacts, cities, sectors, price ranges)
 * - optional enriched Need entity (for edit mode)
 */
export interface NeedFVMParams {
    viewEnums: NeedEnums;
    contactMap: RecordMap<ContactModel>;
    cityMap: RecordMap<CityModel>;
    sectorMap: RecordMap<SectorModel>;
    enrichedNeed?: NeedModel;
    contact? : ContactModel
}


/**
 * =====================================================
 * NeedFormViewModel
 * =====================================================
 *
 * Responsible for:
 * - Mapping Need domain entity to form values
 * - Providing options for dropdowns
 * - Handling dynamic dependencies (e.g., city -> sector)
 * - Generating success/error messages for UI
 * - Providing form title based on edit or creation mode
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export class NeedFormViewModel {

    /** Dynamic dropdown options for sectors filtered by city */
    private _sectorOptions = [] as AppEnum[];

    /** Dropdown options for cities */
    private _cityOptions = [] as AppEnum[];

    /** Related domain lists */
    private _contactMap: RecordMap<ContactModel>;
    private _sectorMap: RecordMap<SectorModel>;

    /** The current Need entity (enriched) */
    private _model?: NeedModel;

    private _priceRangeOptions  = [] as AppEnum[];
    private _operationOptions  = [] as AppEnum[];
    private _categoryOptions = [] as AppEnum[];
    private _typeOptions = [] as AppEnum[];
    private _statusOptions = [] as AppEnum[];
    private _standingOptions = [] as AppEnum[];
    private _contact? : ContactModel;



    /** Constructor – initializes options and maps domain lists */
    public constructor(private params: NeedFVMParams) {
        const {viewEnums, contactMap, sectorMap, enrichedNeed, contact} = params;

        this._cityOptions = OptionHelper.cityOptions(this.params.cityMap.toArray());
        
        this._contactMap = contactMap;
        this._sectorMap = sectorMap;
        this._model = enrichedNeed;
        this._contact = contact;

        this._priceRangeOptions = OptionHelper.sortByMinMax(viewEnums.PriceRangeEnum);

        this._operationOptions = OptionHelper.sortByLabel(viewEnums.OperationTypeEnum);

        this._categoryOptions = OptionHelper.sortByLabel(viewEnums.PropertyCategoryEnum);

        this._typeOptions = OptionHelper.sortByLabel(viewEnums.PropertyTypeEnum);

        this._statusOptions = OptionHelper.sortByLabel(viewEnums.NeedStatusEnum);

        this._standingOptions = OptionHelper.sortByLabel(viewEnums.StandingEnum);

        this.handleCityChange(this._model?.sector?.cityId);
    }

    /**
     * =====================================================
     * Dynamic Dependencies
     * =====================================================
     *
     * Updates sector options based on selected city.
     */
     handleCityChange(newCityId?: number | string | null) {

        this._sectorOptions = newCityId
            ? OptionHelper.sectorOptions(
                this._sectorMap.filter(
                    sector => sector.cityId === Number(newCityId)
                ).toArray())
            : [];

        return this._sectorOptions?.[0]?.value;

    }
   


    /**
     * =====================================================
     * Messages
     * =====================================================
     */
    getError = (error?: string) => error || "Une erreur s'est produite";
    getSuccess = () => this._model ? "Besoin mise à jour avec succés" : "Besoin créé avec succés";

    /**
     * =====================================================
     * Form Values
     * =====================================================
     *
     * Maps the current Need entity to the form model.
     */
    get formValues() {
        return {

            contactId: this._contact?.id ?? this._model?.contact?.id,
            
            operationType: this._model?.operationType,
            
            propertyCategory: this._model?.propertyCategory,
            
            propertyType: this._model?.propertyType,
            
            sectorId: this._model?.sector?.id,
            
            cityId: this._model?.sector?.city?.id,
            
            priceRange: this._model?.priceRange,
            
            propertyStanding: this._model?.propertyStanding,
            
            status: this._model?.status,
            
            desiredStartAt: DateFormatHelper.toInput(this._model?.desiredStartAt),

            maxServiceChargeProvision : SoNumber.formatAmount(this._model?.maxServiceChargeProvision),


             usageType: this._model?.usageType ?? "",
        
        } as NeedFormValues
    }

    get id() {
        return this._model?.id;
    }



    /**
     * =====================================================
     * Dropdown Options
     * =====================================================
     *
     * Exposes select options to the UI layer.
     */
    get operationOptions(): AppEnum[] { return this._operationOptions; }

    get categoryOptions(): AppEnum[] { return this._categoryOptions; }

     get propertyUsageOptions(): AppEnum[] {
        return OptionHelper.sortByLabel(this.params.viewEnums.PropertyUsageEnum);
    }

    get typeOptions(): AppEnum[] { return this._typeOptions; }

    get statusOptions(): AppEnum[] { return this._statusOptions; }

    get standingOptions(): AppEnum[] { return this._standingOptions; }

    get priceRangeOptions(): AppEnum[] { return this._priceRangeOptions;}

    get contactOptions(): AppEnum[] { return OptionHelper.contactOptions(this._contactMap.toArray()); }

    get cityOptions(): AppEnum[] { return this._cityOptions; }

    get sectorOptions(): AppEnum[] { return this._sectorOptions; }
    
    


    /**
     * =====================================================
     * Title
     * =====================================================
     *
     * Generates a UI-friendly title based on edit or creation mode.
     */
    get title() {
        return this._model
            ? "Besoin de " + formatName(this._model?.contact?.person)
            : "Création de besoin";
    }
}